import React, { useState } from 'react';
import {
  Box, Button, Stack, Typography, List, ListItem, ListItemText, Divider, Switch,
  FormControlLabel, Alert, TextField, InputAdornment, Tooltip
} from '@mui/material';
import { settlementsApi, friendGroupsApi } from '../../services/api';
import LoadingButton from '../LoadingButton';
import { toPaise } from '../../utils/splitMath';
import { formatMoney, currencySymbol } from '../../utils/money';

const formatAmount = (value) => formatMoney(Math.abs(Number(value)));
const personName = (person) => (person.isYou ? 'You' : person.name || person.email);

// A group's balances and suggested payments ("simplify debts"), with buttons to
// record payments. `data` comes from GET /settlements/groups/:id; the panel
// hands back fresh data through onData after every change.
function GroupBalancesPanel({ groupId, data, onData, onChanged, showPayments = false }) {
  const [error, setError] = useState('');
  const [recording, setRecording] = useState(null); // { transfer, amount }
  const [saving, setSaving] = useState(false);
  const [togglingSimplify, setTogglingSimplify] = useState(false);

  const handleToggleSimplify = async (event) => {
    const simplifyDebts = event.target.checked;
    try {
      setTogglingSimplify(true);
      setError('');
      await friendGroupsApi.update(groupId, { simplifyDebts });
      const response = await settlementsApi.getGroup(groupId);
      onData(response.data);
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update the group');
    } finally {
      setTogglingSimplify(false);
    }
  };

  const openAmount = recording ? recording.transfer.amount : 0;
  const amountPaise = toPaise(recording?.amount);
  const amountError = !recording
    ? ''
    : !Number.isFinite(amountPaise) || amountPaise <= 0
      ? 'Enter an amount greater than zero'
      : amountPaise > toPaise(openAmount)
        ? `Cannot be more than ${formatAmount(openAmount)}`
        : '';

  const handleRecord = async () => {
    if (!recording || amountError) return;
    try {
      setSaving(true);
      setError('');
      const response = await settlementsApi.settleInGroup(groupId, {
        fromEmail: recording.transfer.from.email,
        toEmail: recording.transfer.to.email,
        amount: Number(recording.amount)
      });
      onData(response.data);
      setRecording(null);
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={data.group.simplifyDebts}
              onChange={handleToggleSimplify}
              disabled={togglingSimplify}
            />
          }
          label="Simplify group debts"
        />
        <Typography variant="body2" color="text.secondary">
          {data.group.simplifyDebts
            ? 'Combines everyone\'s debts into the fewest payments. You may be asked to pay someone you never split with directly.'
            : 'Everyone pays back the person who paid for them.'}
        </Typography>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Suggested payments</Typography>
        {data.transfers.length === 0 ? (
          <Alert severity="info">Everyone in this group is settled up. 🎉</Alert>
        ) : (
          <List disablePadding>
            {data.transfers.map((transfer, index) => {
              const key = `${transfer.from.email}->${transfer.to.email}`;
              const isRecording = recording && `${recording.transfer.from.email}->${recording.transfer.to.email}` === key;
              return (
                <React.Fragment key={key}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem disableGutters sx={{ display: 'block' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                      <ListItemText
                        primary={`${personName(transfer.from)} ${transfer.from.isYou ? 'pay' : 'pays'} ${personName(transfer.to)}`}
                        secondary={transfer.from.isYou || transfer.to.isYou ? null : 'Between other members'}
                      />
                      <Typography
                        fontWeight="bold"
                        color={transfer.from.isYou ? 'error.main' : transfer.to.isYou ? 'success.main' : 'text.primary'}
                      >
                        {formatAmount(transfer.amount)}
                      </Typography>
                      {!isRecording && (
                        <Tooltip title="Record that this payment was made">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setRecording({ transfer, amount: transfer.amount.toFixed(2) })}
                          >
                            Record
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                    {isRecording && (
                      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          label="Amount paid"
                          value={recording.amount}
                          onChange={(e) => setRecording({ ...recording, amount: e.target.value })}
                          error={!!amountError}
                          helperText={amountError || (amountPaise < toPaise(openAmount) ? 'Recorded as a partial payment' : ' ')}
                          slotProps={{
                            input: { startAdornment: <InputAdornment position="start">{currencySymbol()}</InputAdornment> },
                            htmlInput: { min: 0, step: '0.01' }
                          }}
                        />
                        <Button size="small" onClick={() => setRecording(null)}>Cancel</Button>
                        <LoadingButton
                          size="small"
                          variant="contained"
                          loading={saving}
                          disabled={!!amountError}
                          onClick={handleRecord}
                        >
                          Save
                        </LoadingButton>
                      </Stack>
                    )}
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Members</Typography>
        <List dense disablePadding>
          {data.members.map((member) => (
            <ListItem key={member.email} disableGutters>
              <ListItemText primary={personName(member)} secondary={member.isYou ? null : member.email} />
              <Typography
                variant="body2"
                color={member.net > 0 ? 'success.main' : member.net < 0 ? 'error.main' : 'text.secondary'}
              >
                {member.net > 0
                  ? `gets back ${formatAmount(member.net)}`
                  : member.net < 0
                    ? `owes ${formatAmount(member.net)}`
                    : 'settled up'}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Box>

      {showPayments && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Payments in this group</Typography>
          {data.payments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No payments recorded yet.</Typography>
          ) : (
            <List dense disablePadding>
              {data.payments.map((payment) => (
                <ListItem key={payment.id} disableGutters>
                  <ListItemText
                    primary={`${personName(payment.from)} paid ${personName(payment.to)}`}
                    secondary={new Date(payment.settledAt).toLocaleDateString()}
                  />
                  <Typography variant="body2" fontWeight="bold">{formatAmount(payment.amount)}</Typography>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Stack>
  );
}

export default GroupBalancesPanel;
